import React from 'react';

export default function UserManagement({ 
  users, 
  userFilters, 
  setUserFilters, 
  userPage, 
  setUserPage, 
  usersPerPage, 
  selectedUsers, 
  setSelectedUsers, 
  toggleUserSelection, 
  handleViewUser, 
  handleEditUser, 
  handleDeleteUser 
}) {
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(userFilters.search.toLowerCase()) || 
                         (user.name && user.name.toLowerCase().includes(userFilters.search.toLowerCase()));
    const matchesRole = !userFilters.role || user.role === userFilters.role;
    const matchesStatus = !userFilters.status || (userFilters.status === 'active' ? user.is_active : !user.is_active);
    return matchesSearch && matchesRole && matchesStatus;
  });

  const totalUserPages = Math.ceil(filteredUsers.length / usersPerPage);
  const currentUsers = filteredUsers.slice((userPage - 1) * usersPerPage, userPage * usersPerPage);

  const truncateText = (text, maxLength) => {
    if (!text) return '—';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <>
      <div className="admin-card-header">
        <h3>User Management</h3>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <div style={{ fontSize: '0.85rem', color: '#64748b', alignSelf: 'center', marginRight: '0.5rem' }}>
            Total: {filteredUsers.length} users
          </div>
          <input 
            type="text" 
            placeholder="Search users..." 
            value={userFilters.search}
            onChange={(e) => {
              setUserFilters(f => ({ ...f, search: e.target.value }));
              setUserPage(1);
            }}
            style={{ padding: '0.4rem 0.8rem', borderRadius: '0.4rem', border: '1px solid #e2e8f0', fontSize: '0.85rem' }}
          />
          <select 
            value={userFilters.role}
            onChange={(e) => {
              setUserFilters(f => ({ ...f, role: e.target.value }));
              setUserPage(1);
            }}
            style={{ padding: '0.4rem 0.8rem', borderRadius: '0.4rem', border: '1px solid #e2e8f0', fontSize: '0.85rem' }}
          >
            <option value="">All Roles</option>
            <option value="ADMIN">Admin</option>
            <option value="USER">User</option>
          </select>
        </div>
      </div>

      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ width: '40px', padding: '1rem 1.5rem' }}>
                <input 
                  type="checkbox" 
                  checked={filteredUsers.length > 0 && selectedUsers.length === filteredUsers.length}
                  onChange={() => {
                    if (selectedUsers.length === filteredUsers.length) setSelectedUsers([]);
                    else setSelectedUsers(filteredUsers.map(u => u.id));
                  }}
                />
              </th>
              <th style={{ padding: '1rem 1.5rem' }}>Full Name</th>
              <th style={{ padding: '1rem 1.5rem' }}>Email Address</th>
              <th style={{ padding: '1rem 1.5rem' }}>Role</th>
              <th style={{ padding: '1rem 1.5rem' }}>Status</th>
              <th style={{ padding: '1rem 1.5rem' }}>Joined Date</th>
              <th style={{ padding: '1rem 1.5rem', width: '120px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.map((user) => (
              <tr key={user.id}>
                <td style={{ padding: '1rem 1.5rem' }}>
                  <input 
                    type="checkbox" 
                    checked={selectedUsers.includes(user.id)}
                    onChange={() => toggleUserSelection(user.id)}
                  />
                </td>
                <td style={{ padding: '1rem 1.5rem' }}>
                  <div style={{ fontWeight: 600, color: '#0f172a' }} title={user.name}>
                    {truncateText(user.name || 'User', 12)}
                  </div>
                </td>
                <td style={{ padding: '1rem 1.5rem' }}>
                  <div style={{ color: '#64748b', fontSize: '0.9rem' }} title={user.email}>
                    {truncateText(user.email, 20)}
                  </div>
                </td>
                <td style={{ padding: '1rem 1.5rem' }}>
                  <span className="tag-pill" style={{ background: '#f1f5f9', color: '#475569', fontWeight: 600 }}>
                    {user.role}
                  </span>
                </td>
                <td style={{ padding: '1rem 1.5rem' }}>
                  <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                    {user.is_active ? '● Active' : '● Inactive'}
                  </span>
                </td>
                <td style={{ padding: '1rem 1.5rem' }}>{new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                <td style={{ padding: '1rem 1.5rem' }}>
                  <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center' }}>
                    <button 
                      className="action-btn" 
                      title="View" 
                      onClick={() => handleViewUser(user.id)}
                      style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '1.2rem', padding: 0, display: 'flex', alignItems: 'center' }}
                    >
                      👁️
                    </button>
                    <button 
                      className="action-btn" 
                      title="Edit" 
                      onClick={() => handleEditUser(user.id)}
                      style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '1.2rem', padding: 0, display: 'flex', alignItems: 'center' }}
                    >
                      ✏️
                    </button>
                    <button 
                      className="action-btn" 
                      title="Delete" 
                      onClick={() => handleDeleteUser(user.id)}
                      style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '1.2rem', padding: 0, display: 'flex', alignItems: 'center' }}
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {totalUserPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {(userPage - 1) * usersPerPage + 1} to {Math.min(userPage * usersPerPage, filteredUsers.length)} of {filteredUsers.length} users
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              disabled={userPage === 1}
              onClick={() => setUserPage(userPage - 1)}
            >
              Previous
            </button>
            {Array.from({ length: Math.min(totalUserPages, 5) }, (_, i) => {
              let pageNum;
              if (totalUserPages <= 5) {
                pageNum = i + 1;
              } else if (userPage <= 3) {
                pageNum = i + 1;
              } else if (userPage >= totalUserPages - 2) {
                pageNum = totalUserPages - 4 + i;
              } else {
                pageNum = userPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  className={`pagination-btn ${userPage === pageNum ? 'active' : ''}`}
                  onClick={() => setUserPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              className="pagination-btn"
              disabled={userPage >= totalUserPages}
              onClick={() => setUserPage(userPage + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </>
  );
}

